import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { athleteNotesApi } from '../../api/athleteNotes';
import { NOTE_TYPE_CONFIG, type NoteType } from '../../types/athleteNote';
import { Button } from '../ui';
import { useAuthStore } from '../../store/authStore';

interface AddAthleteNoteSheetProps {
  athleteId: string;
  onClose: () => void;
}

export const AddAthleteNoteSheet: React.FC<AddAthleteNoteSheetProps> = ({
  athleteId,
  onClose,
}) => {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const [noteType, setNoteType] = useState<NoteType>('observation');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      athleteNotesApi.create(athleteId, {
        assistant_id: currentAssistantId!,
        title,
        content,
        note_type: noteType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete-notes', athleteId] });
      // Invalidate activity feed - all note types now appear in activity feed
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    createMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl flex flex-col"
        style={{ backgroundColor: 'var(--bg-primary)', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 px-4 py-4 border-b"
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderColor: 'var(--bg-secondary)',
          }}
        >
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Новая заметка
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg"
              style={{ color: 'var(--text-hint)' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Note type selector */}
          <div>
            <label
              className="block text-[13px] font-medium mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Тип заметки
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(NOTE_TYPE_CONFIG).map(([type, config]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setNoteType(type as NoteType)}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor:
                      noteType === type
                        ? config.color
                        : 'var(--bg-secondary)',
                    color: noteType === type ? '#FFFFFF' : 'var(--text-primary)',
                  }}
                >
                  {config.icon} {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label
              htmlFor="title"
              className="block text-[13px] font-medium mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Заголовок
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Краткое описание"
              maxLength={200}
              className="w-full px-4 py-3 rounded-xl text-[15px] border-none outline-none"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              required
            />
          </div>

          {/* Content */}
          <div>
            <label
              htmlFor="content"
              className="block text-[13px] font-medium mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Описание
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Подробное описание..."
              maxLength={10000}
              rows={6}
              className="w-full px-4 py-3 rounded-xl text-[15px] border-none outline-none resize-none"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              required
            />
            <p
              className="text-[11px] mt-1 text-right"
              style={{ color: 'var(--text-hint)' }}
            >
              {content.length} / 10000
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              disabled={!title.trim() || !content.trim() || createMutation.isPending}
              className="flex-1"
            >
              {createMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
